import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Save, Loader2, Image as ImageIcon, FileText } from "lucide-react";
import { fileToDataUrl } from "@/lib/sales";

// Edit a sales-collection lead (any status) + optionally its Won packet.
// Available to any user with edit rights on the lead (assigned executive,
// reviewing manager, director, admin). Works AFTER "Won" too so Interior
// Consultants / IC Managers can correct client details post-conversion.
export default function EditSalesLeadDialog({ open, lead, onClose, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open || !lead) return;
    setErr("");
    const w = lead.won_data || null;
    setForm({
      client_name: lead.client_name || "",
      phone: lead.phone || "",
      email: lead.email || "",
      source: lead.source || "",
      notes: lead.notes || "",
      project_name: lead.project_name || (w?.project_name || ""),
      // Won packet fields (only used when lead.status === 'won')
      w_invoice_name: w?.invoice_name || "",
      w_project_name: w?.project_name || "",
      w_flat_no: w?.flat_no || "",
      w_address: w?.address || "",
      w_city: w?.city || "",
      w_project_amount: w?.project_amount ?? "",
      w_amount_received: w?.amount_received ?? "",
      w_sales_mom: w?.sales_mom || "",
      w_floor_plan_pdf: w?.floor_plan_pdf || "",
      w_initial_quotation_pdf: w?.initial_quotation_pdf || "",
      w_reference_images: Array.isArray(w?.reference_images) ? w.reference_images : [],
    });
  }, [open, lead]);

  if (!open || !lead || !form) return null;

  const isWon = lead.status === "won";

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const onFloorPlan = async (files) => {
    if (!files?.[0]) return;
    setField("w_floor_plan_pdf", await fileToDataUrl(files[0]));
  };
  const onInitialQuotation = async (files) => {
    if (!files?.[0]) return;
    setField("w_initial_quotation_pdf", await fileToDataUrl(files[0]));
  };
  const onRefImgs = async (files) => {
    const arr = Array.from(files || []).slice(0, 6);
    const urls = await Promise.all(arr.map(fileToDataUrl));
    setField("w_reference_images", [...form.w_reference_images, ...urls].slice(0, 8));
  };
  const removeRefImg = (idx) => {
    setField("w_reference_images", form.w_reference_images.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim() || !form.phone.trim()) {
      setErr("Client name and phone are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        client_name: form.client_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        source: form.source.trim() || null,
        notes: form.notes.trim() || null,
        project_name: form.project_name.trim() || null,
      };
      if (isWon) {
        payload.won_data = {
          invoice_name: form.w_invoice_name.trim() || undefined,
          project_name: form.w_project_name.trim() || undefined,
          flat_no: form.w_flat_no.trim() || undefined,
          address: form.w_address.trim() || undefined,
          city: form.w_city.trim() || undefined,
          project_amount: form.w_project_amount === "" ? undefined : Number(form.w_project_amount),
          amount_received: form.w_amount_received === "" ? undefined : Number(form.w_amount_received),
          sales_mom: form.w_sales_mom.trim() || undefined,
          floor_plan_pdf: form.w_floor_plan_pdf || undefined,
          initial_quotation_pdf: form.w_initial_quotation_pdf || undefined,
          reference_images: form.w_reference_images,
        };
      }
      const { data } = await api.patch(`/sales/leads/${lead.id}`, payload);
      toast.success("Lead updated");
      onSaved?.(data);
      onClose();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="edit-sales-lead-dialog">
      <div className="w-full max-w-2xl iz-card p-8 relative max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500" data-testid="edit-sales-lead-close">
          <X className="w-4 h-4"/>
        </button>
        <div className="mb-6">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Edit</p>
          <h2 className="font-display text-2xl text-white">{isWon ? "Won Lead · Correct Details" : "Sales Lead"}</h2>
          <p className="text-[11px] text-gray-500 font-mono">
            {isWon
              ? "Update client details and Won attachments — changes propagate to Design + Client Portal."
              : "Update client contact and project details."}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {/* Basic client details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Client Name *" value={form.client_name} onChange={v => setField("client_name", v)} testid="esl-client-name"/>
            <Field label="Phone *" value={form.phone} onChange={v => setField("phone", v)} testid="esl-phone"/>
            <Field label="Email" value={form.email} onChange={v => setField("email", v)} type="email" testid="esl-email"/>
            <Field label="Project Name" value={form.project_name} onChange={v => setField("project_name", v)} testid="esl-project"/>
            <Field label="Source" value={form.source} onChange={v => setField("source", v)} testid="esl-source"/>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Notes</label>
            <textarea value={form.notes} onChange={(e) => setField("notes", e.target.value)} className="iz-input" rows={2} data-testid="esl-notes"/>
          </div>

          {/* Won Packet */}
          {isWon && (
            <div className="pt-4 border-t border-[#1a1a1a] space-y-4" data-testid="esl-won-section">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Won Packet</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Invoice Name" value={form.w_invoice_name} onChange={v => setField("w_invoice_name", v)} testid="esl-won-invoice"/>
                <Field label="Project Name (Won)" value={form.w_project_name} onChange={v => setField("w_project_name", v)} testid="esl-won-project"/>
                <Field label="Flat No" value={form.w_flat_no} onChange={v => setField("w_flat_no", v)} testid="esl-won-flat"/>
                <Field label="City" value={form.w_city} onChange={v => setField("w_city", v)} testid="esl-won-city"/>
                <div className="sm:col-span-2">
                  <Field label="Address" value={form.w_address} onChange={v => setField("w_address", v)} testid="esl-won-address"/>
                </div>
                <Field label="Project Amount (₹)" value={form.w_project_amount} onChange={v => setField("w_project_amount", v)} type="number" testid="esl-won-amount"/>
                <Field label="Received (₹)" value={form.w_amount_received} onChange={v => setField("w_amount_received", v)} type="number" testid="esl-won-received"/>
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">Sales MOM</label>
                <textarea value={form.w_sales_mom} onChange={(e) => setField("w_sales_mom", e.target.value)} className="iz-input" rows={2} data-testid="esl-won-mom"/>
              </div>

              {/* Attachments */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FileSwap label="Floor Plan PDF"
                          current={form.w_floor_plan_pdf}
                          accept="application/pdf"
                          onChange={onFloorPlan}
                          testid="esl-floor-plan"/>
                <FileSwap label="Initial Quotation PDF"
                          current={form.w_initial_quotation_pdf}
                          accept="application/pdf"
                          onChange={onInitialQuotation}
                          testid="esl-init-quot"/>
              </div>

              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">
                  Reference Images <span className="text-gray-600 normal-case">(optional · up to 8)</span>
                </label>
                {form.w_reference_images.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mb-2" data-testid="esl-refimgs-grid">
                    {form.w_reference_images.map((src, i) => (
                      <div key={i} className="relative group">
                        <img src={src} alt="" className="w-full h-16 object-cover rounded border border-[#262626]"/>
                        <button type="button" onClick={() => removeRefImg(i)}
                                className="absolute -top-1.5 -right-1.5 bg-black border border-[#262626] text-gray-400 hover:text-red-400 rounded-full w-5 h-5 flex items-center justify-center text-[10px]"
                                data-testid={`esl-refimg-remove-${i}`}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="iz-btn-secondary inline-flex items-center gap-2 cursor-pointer" data-testid="esl-refimgs-add">
                  <ImageIcon className="w-4 h-4"/> Add images
                  <input type="file" accept="image/*" multiple hidden onChange={(e) => onRefImgs(e.target.files)}/>
                </label>
              </div>
            </div>
          )}

          {err && <p className="text-xs text-red-400" data-testid="esl-error">{err}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="iz-btn-secondary" data-testid="esl-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary flex items-center gap-2" data-testid="esl-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>}
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">{label}</label>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="iz-input" type={type} data-testid={testid}/>
    </div>
  );
}

function FileSwap({ label, current, accept, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-1">{label}</label>
      <label className="iz-btn-secondary inline-flex items-center gap-2 cursor-pointer w-full justify-center" data-testid={testid}>
        <FileText className="w-4 h-4"/>
        {current ? "Replace" : "Upload"}
        <input type="file" accept={accept} hidden onChange={(e) => onChange(e.target.files)}/>
      </label>
      {current && (
        <a href={current} target="_blank" rel="noreferrer" className="mt-1 block text-[10px] text-[#d4af37] hover:underline truncate" data-testid={`${testid}-current`}>
          View current
        </a>
      )}
    </div>
  );
}
